import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './P2pMerchantSecurityDeposit.css';

// Mock data for security deposit
interface DepositTransaction {
  id: string;
  type: 'lock' | 'unlock';
  amount: number;
  description: string;
  date: string;
}

interface AddDepositModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  depositAmount: string;
  onDepositAmountChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  availableBalance: number;
  isValid: boolean;
}

const AddDepositModal: React.FC<AddDepositModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  depositAmount,
  onDepositAmountChange,
  availableBalance,
  isValid
}) => {
  if (!isOpen) return null;

  return (
    <div className="merchant-security-deposit-modal-overlay" onClick={onClose}>
      <div className="merchant-security-deposit-add-modal" onClick={(e) => e.stopPropagation()}>
        <div className="merchant-security-deposit-add-modal-header">
          <h3 className="merchant-security-deposit-add-modal-title">Add Security Deposit</h3>
          <button
            className="merchant-security-deposit-add-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="merchant-security-deposit-add-modal-body">
          <p className="merchant-security-deposit-add-modal-description">
            Enter the amount of USDT you want to add to your security deposit.
          </p>

          <div className="merchant-security-deposit-add-modal-input-wrapper">
            <input
              type="text"
              className="merchant-security-deposit-add-modal-input"
              value={depositAmount}
              onChange={onDepositAmountChange}
              placeholder="0.00000000"
            />
            <span className="merchant-security-deposit-add-modal-currency">USDT</span>
          </div>

          <div className="merchant-security-deposit-add-modal-balance">
            Available: {availableBalance.toFixed(8)} USDT
          </div>
        </div>

        <div className="merchant-security-deposit-add-modal-actions">
          <button
            className="merchant-security-deposit-add-modal-btn merchant-security-deposit-add-modal-btn-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="merchant-security-deposit-add-modal-btn merchant-security-deposit-add-modal-btn-confirm"
            onClick={onConfirm}
            disabled={!isValid}
          >
            Add Deposit
          </button>
        </div>
      </div>
    </div>
  );
};

interface SuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SuccessModal: React.FC<SuccessModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="merchant-security-deposit-modal-overlay" onClick={onClose}>
      <div className="merchant-security-deposit-success-modal" onClick={(e) => e.stopPropagation()}>
        <button
          className="merchant-security-deposit-success-modal-close"
          onClick={onClose}
          aria-label="Close modal"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>

        <div className="merchant-security-deposit-success-icon">
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
            <path d="M10.5 18L15.75 23.25L25.5 12.75" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>

        <h3 className="merchant-security-deposit-success-title">Success!</h3>
        <p className="merchant-security-deposit-success-message">Deposit added successfully!</p>

        <button
          className="merchant-security-deposit-success-btn"
          onClick={onClose}
        >
          Done
        </button>
      </div>
    </div>
  );
};

interface RequestWithdrawalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  reason: string;
  onReasonChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

const RequestWithdrawalModal: React.FC<RequestWithdrawalModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  reason,
  onReasonChange
}) => {
  if (!isOpen) return null;

  return (
    <div className="merchant-security-deposit-modal-overlay" onClick={onClose}>
      <div className="merchant-security-deposit-withdrawal-modal" onClick={(e) => e.stopPropagation()}>
        <button
          className="merchant-security-deposit-withdrawal-modal-close"
          onClick={onClose}
          aria-label="Close modal"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>

        <h3 className="merchant-security-deposit-withdrawal-modal-title">Request Withdrawal</h3>

        <p className="merchant-security-deposit-withdrawal-modal-description">
          Request to withdraw your security deposit. Please note that withdrawal may affect your merchant status.
        </p>

        <textarea
          className="merchant-security-deposit-withdrawal-modal-textarea"
          value={reason}
          onChange={onReasonChange}
          placeholder="Reason for withdrawal (optional)"
          rows={4}
        />

        <div className="merchant-security-deposit-withdrawal-modal-actions">
          <button
            className="merchant-security-deposit-withdrawal-modal-btn merchant-security-deposit-withdrawal-modal-btn-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="merchant-security-deposit-withdrawal-modal-btn merchant-security-deposit-withdrawal-modal-btn-confirm"
            onClick={() => onConfirm(reason)}
          >
            Request Withdrawal
          </button>
        </div>
      </div>
    </div>
  );
};

const WithdrawalSuccessModal: React.FC<SuccessModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="merchant-security-deposit-modal-overlay" onClick={onClose}>
      <div className="merchant-security-deposit-success-modal" onClick={(e) => e.stopPropagation()}>
        <button
          className="merchant-security-deposit-success-modal-close"
          onClick={onClose}
          aria-label="Close modal"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>

        <div className="merchant-security-deposit-success-icon merchant-security-deposit-success-icon-green">
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
            <path d="M10.5 18L15.75 23.25L25.5 12.75" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>

        <h3 className="merchant-security-deposit-success-title">Success!</h3>
        <p className="merchant-security-deposit-success-message">
          Your security deposit withdrawal request has been received. Please wait for confirmation
        </p>

        <button
          className="merchant-security-deposit-success-btn merchant-security-deposit-success-btn-green"
          onClick={onClose}
        >
          Done
        </button>
      </div>
    </div>
  );
};

const mockDepositData = {
  currentDeposit: 1.00000000,
  requiredDeposit: 1.00000000,
  progress: 100.0,
  status: 'Active',
  requirementMet: true,
};

const mockTransactions: DepositTransaction[] = [
  {
    id: '8574',
    type: 'lock',
    amount: 1.00000000,
    description: 'MERCHANT DEPOSIT LOCK',
    date: '2024-03-15 10:30 AM',
  },
  {
    id: '8543',
    type: 'unlock',
    amount: 201.00000000,
    description: 'MERCHANT DEPOSIT UNLOCK',
    date: '2024-03-14 2:15 PM',
  },
];

const P2pMerchantSecurityDeposit: React.FC = () => {
  const navigate = useNavigate();
  const [isAddDepositModalOpen, setIsAddDepositModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isWithdrawalModalOpen, setIsWithdrawalModalOpen] = useState(false);
  const [isWithdrawalSuccessModalOpen, setIsWithdrawalSuccessModalOpen] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [withdrawalReason, setWithdrawalReason] = useState('');

  // Mock data for available balance
  const mockAvailableBalance = 500.00000000;

  const handleAddDeposit = () => {
    setIsAddDepositModalOpen(true);
    setDepositAmount('');
  };

  const handleRequestWithdrawal = () => {
    setIsWithdrawalModalOpen(true);
    setWithdrawalReason('');
  };

  const handleCloseWithdrawalModal = () => {
    setIsWithdrawalModalOpen(false);
    setWithdrawalReason('');
  };

  const handleConfirmWithdrawal = (reason: string) => {
    // In a real app, this would send the withdrawal request to backend
    console.log('Requesting withdrawal with reason:', reason);
    setIsWithdrawalModalOpen(false);
    setWithdrawalReason('');
    setIsWithdrawalSuccessModalOpen(true);
  };

  const handleCloseWithdrawalSuccessModal = () => {
    setIsWithdrawalSuccessModalOpen(false);
  };

  const handleWithdrawalReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setWithdrawalReason(e.target.value);
  };

  const handleCloseAddDepositModal = () => {
    setIsAddDepositModalOpen(false);
    setDepositAmount('');
  };

  const handleConfirmAddDeposit = () => {
    // In a real app, this would send the deposit request to backend
    console.log('Adding deposit:', depositAmount);
    setIsAddDepositModalOpen(false);
    setDepositAmount('');
    setIsSuccessModalOpen(true);
  };

  const handleCloseSuccessModal = () => {
    setIsSuccessModalOpen(false);
  };

  const handleDepositAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow only numbers and decimal point with up to 8 decimal places
    if (/^\d*\.?\d{0,8}$/.test(value) || value === '') {
      setDepositAmount(value);
    }
  };

  const isDepositAmountValid = () => {
    const amount = parseFloat(depositAmount);
    return !isNaN(amount) && amount > 0 && amount <= mockAvailableBalance;
  };

  return (
    <div className="merchant-security-deposit">
      {/* Header */}
      <header className="merchant-security-deposit-header">
              <button 
                className="merchant-security-deposit-back-button" 
                onClick={() => navigate(-1)} 
                aria-label="Go back"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <h1 className="merchant-security-deposit-title">Security Deposit</h1>
            </header>

            {/* Main Deposit Card */}
            <div className="merchant-security-deposit-card">
              <div className="deposit-card-header">
                <div className="deposit-card-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2L21 7V12C21 17 12 22 12 22C12 22 3 17 3 12V7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                    <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h2 className="deposit-card-title">
                  Security Deposit<br />Status
                </h2>
                <div className="deposit-status-badge">
                  {mockDepositData.status}
                </div>
              </div>

              <div className="deposit-amounts">
                <div className="deposit-amount-item">
                  <span className="deposit-amount-label">Current Deposit</span>
                  <span className="deposit-amount-value">{mockDepositData.currentDeposit.toFixed(8)}</span>
                  <span className="deposit-amount-currency">USDT</span>
                </div>
                <div className="deposit-amount-item">
                  <span className="deposit-amount-label">Required</span>
                  <span className="deposit-amount-value">{mockDepositData.requiredDeposit.toFixed(8)}</span>
                  <span className="deposit-amount-currency">USDT</span>
                </div>
              </div>

              <div className="deposit-progress-section">
                <div className="deposit-progress-header">
                  <span className="deposit-progress-label">Progress</span>
                  <span className="deposit-progress-value">{mockDepositData.progress}%</span>
                </div>
                <div className="deposit-progress-bar-container">
                  <div 
                    className="deposit-progress-bar" 
                    style={{ width: `${mockDepositData.progress}%` }}
                  ></div>
                </div>
              </div>

              {mockDepositData.requirementMet && (
                <div className="deposit-requirement-badge">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M5 8L7 10L11 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Security deposit requirement met</span>
                </div>
              )}

              <div className="deposit-action-buttons">
                <button 
                  className="deposit-action-button deposit-action-button-primary"
                  onClick={handleAddDeposit}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <span>Add Deposit</span>
                </button>
                <button 
                  className="deposit-action-button deposit-action-button-secondary"
                  onClick={handleRequestWithdrawal}
                >
                  <span>Request Withdrawal</span>
                </button>
              </div>
            </div>

            {/* Transaction History */}
            <div className="merchant-security-deposit-history">
              <div className="deposit-history-header">
                <div className="deposit-history-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                    <path d="M3 10H21M8 14H8.01M12 14H12.01M16 14H16.01M8 18H8.01M12 18H12.01M16 18H16.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <h2 className="deposit-history-title">Transaction History</h2>
              </div>

              <div className="deposit-history-list">
                {mockTransactions.map((transaction, index) => (
                  <div key={index} className="deposit-transaction-card">
                    <div className="deposit-transaction-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        {transaction.type === 'lock' ? (
                          <path d="M12 2L21 7V12C21 17 12 22 12 22C12 22 3 17 3 12V7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                        ) : (
                          <path d="M12 2V22M12 2L6 8M12 2L18 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        )}
                      </svg>
                    </div>
                    <div className="deposit-transaction-content">
                      <div className="deposit-transaction-amount">
                        {transaction.type === 'lock' ? '+' : '+'}{transaction.amount.toFixed(8)}
                      </div>
                      <div className="deposit-transaction-description">
                        {transaction.description}
                      </div>
                      <div className="deposit-transaction-footer">
                        <span className="deposit-transaction-id">ID: {transaction.id}</span>
                        <span className="deposit-transaction-date">{transaction.date}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

      <AddDepositModal
        isOpen={isAddDepositModalOpen}
        onClose={handleCloseAddDepositModal}
        onConfirm={handleConfirmAddDeposit}
        depositAmount={depositAmount}
        onDepositAmountChange={handleDepositAmountChange}
        availableBalance={mockAvailableBalance}
        isValid={isDepositAmountValid()}
      />

      <SuccessModal
        isOpen={isSuccessModalOpen}
        onClose={handleCloseSuccessModal}
      />

      <RequestWithdrawalModal
        isOpen={isWithdrawalModalOpen}
        onClose={handleCloseWithdrawalModal}
        onConfirm={handleConfirmWithdrawal}
        reason={withdrawalReason}
        onReasonChange={handleWithdrawalReasonChange}
      />

      <WithdrawalSuccessModal
        isOpen={isWithdrawalSuccessModalOpen}
        onClose={handleCloseWithdrawalSuccessModal}
      />
    </div>
  );
};

export default P2pMerchantSecurityDeposit;